/*
 * A4Cart — the cart-write transport for the ScribblePop party pack (A4, the
 * automatic discount "Party pack: 25% off 4+ tablets"), plus <a4-cart-tiles>,
 * its first caller: the tile row under the last tablet line in the drawer and
 * on /cart (snippets/cart-a4-tiles.liquid). Session 2's collection + PDP band
 * calls the same two methods.
 * Spec: docs/SCRIBBLEPOP_A4_BUILD.md §2 (the sync design) and §4 step 1.
 *
 * ── THE CART IS THE ONLY STATE (§2.1) ────────────────────────────────────────
 * Nothing here knows a price, a percentage or a count. A write goes to the cart,
 * the same request asks the Section Rendering API for every cart region on the
 * page (CartRegions.sectionsToRender()), and each region is swapped from that
 * response. The tiles and the band are Liquid renders of the cart, so they
 * cannot disagree with it or with each other.
 *
 * ── THE §2.4 RULES THIS FILE OWNS ────────────────────────────────────────────
 *   3  No line-item properties. A property splits one variant into two cart
 *      lines, so a tablet added here lands on the same line a PDP add would.
 *      cart-attach-add.js writes _attach_source; this file never writes any.
 *      A4 uptake is read off orders by the discount's title instead.
 *   4  Decrements go by line KEY — change(lineKey, quantity) — never by variant
 *      id: /cart/change.js with a variant id edits the first matching line only.
 *   5  One write in flight per page. While one is pending, write() refuses a
 *      second and every [data-a4-control] is aria-disabled, because an
 *      out-of-order response would paint a stale cart. It also takes Dawn's own
 *      line-list lock (cart__items--disabled, cart.js enableLoading) and treats
 *      that lock as busy, so a line stepper and an A4 control can never
 *      interleave in either direction.
 *   6  It never opens the drawer. Confirmation is the re-render, the bubble and
 *      the caller's live-region announcement.
 *
 * ── WHEN THE WRITE'S OWN RESPONSE CANNOT BE TRUSTED (review R-06, R-10, R-21) ─
 * Three cases end in the same move — one GET of every region on the page
 * (CartRegions.fetchAndRender) and a second swap — because a photograph the
 * response did not carry, or that another writer has already overtaken, must
 * never be left on screen:
 *   - `sections` came back null: the API refused the list (past five ids).
 *   - the write failed: the likeliest reason is that this page is stale — a "−"
 *     carrying a line key another tab has since retired ("no valid id or line
 *     parameter"). The regions are fetched again BEFORE the shopper can tap
 *     again, and the raw body is never shown: Shopify's own shopper-facing
 *     description (a 422, "All 1 Jelly Pink are in your cart." — the text the
 *     buy box shows too) is kept; anything else is window.cartStrings.error.
 *   - another writer announced during our flight (`raced`): the buy box and the
 *     grid cards hold no lock, so two writes can be in flight at once and their
 *     responses land in either order. Each carries a photograph from a
 *     different instant; whichever lands last paints. The buy box publishes
 *     cartUpdate before it paints, so this file always hears the collision, and
 *     after its own swap takes one fresh photograph: the last paint is then the
 *     newest, whatever the arrival order. Dawn's buy box is untouched.
 *
 * ── WHY NOT cart-attach-add.js ────────────────────────────────────────────────
 * Same transport shape, different semantics: that element writes its
 * _attach_source property and moves focus to the added LINE. Keeping A4 in its
 * own file means neither can change under the other.
 *
 * ── source: 'cart-items' ─────────────────────────────────────────────────────
 * The render-suppression flag, not provenance — read cart-attach-add.js's publish
 * site. Every cart region is already rendered from this response, so no
 * subscriber may refetch one (CartItems / CartDrawerItems early-return on it).
 * Provenance rides on `origin: 'a4'`.
 *
 * Globals used: routes / fetchConfig / publish / subscribe / PUB_SUB_EVENTS /
 * trapFocus / window.cartStrings (constants.js, pubsub.js, global.js,
 * layout/theme.liquid — all deferred ahead of this file, so they exist when it
 * runs) and CartRegions (assets/cart-regions.js, tagged just before this file
 * at both load points). Loaded with defer from snippets/cart-drawer.liquid and
 * sections/main-cart-items.liquid — twice on /cart, hence both guards.
 */
if (!window.A4Cart) {
  window.A4Cart = (() => {
    const CONTROL = '[data-a4-control]';
    const DAWN_LOCK = 'cart__items--disabled';
    let inFlight = false;
    let raced = false;

    const busy = () => inFlight || !!document.querySelector(`.${DAWN_LOCK}`);
    const generic = () => (window.cartStrings && window.cartStrings.error) || 'There was an error while updating your cart. Please try again.';

    // R-10: any writer but this one announcing while a write is in flight means
    // two photographs are on the wire. Our own announcement carries origin 'a4'.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (inFlight && (!event || event.origin !== 'a4')) raced = true;
      });
    } else {
      console.warn('A4Cart: pubsub.js is not loaded, so a write that races the buy box cannot be detected and the bag count may end one short (review R-10).');
    }

    /*
     * Only controls that were enabled get locked, and only those get unlocked,
     * so a control the server rendered disabled (a band "−" at zero, say) stays
     * disabled. Nodes the swap replaced are gone by the time this unlocks; the
     * new ones arrive enabled from the server.
     */
    const setLocked = (locked) => {
      if (locked) {
        document.querySelectorAll(CONTROL).forEach((control) => {
          if (control.getAttribute('aria-disabled') === 'true') return;
          control.setAttribute('aria-disabled', 'true');
          control.setAttribute('data-a4-locked', '');
        });
      } else {
        document.querySelectorAll('[data-a4-locked]').forEach((control) => {
          control.removeAttribute('aria-disabled');
          control.removeAttribute('data-a4-locked');
        });
      }
      ['main-cart-items', 'CartDrawer-CartItems'].forEach((id) => {
        const list = document.getElementById(id);
        if (list) list.classList.toggle(DAWN_LOCK, locked);
      });
    };

    /*
     * Focus first, because the swap detached whatever the shopper had focused;
     * in the drawer that also re-arms Dawn's trap, without which Escape stops
     * reaching <cart-drawer>. The announcement lands one tick later: the live
     * region is a node the swap just created, and a status region filled in the
     * same task it was inserted is not reliably announced.
     */
    const afterRender = (opts, announce) => {
      const target = typeof opts.refocus === 'function' ? opts.refocus() : null;
      if (target) {
        if (opts.inDrawer) {
          trapFocus(document.getElementById('CartDrawer'), target);
        } else {
          target.focus();
        }
      }

      const text = announce && typeof opts.announce === 'function' ? opts.announce() : '';
      if (!opts.liveRegion || !text) return;
      setTimeout(() => {
        const region = document.getElementById(opts.liveRegion);
        if (region) region.textContent = text;
      }, 100);
    };

    const write = (url, payload, options) => {
      const opts = options || {};
      if (busy()) return Promise.resolve({ ok: false, skipped: true });

      // Resolved once, before the fetch — same reason as cart.js updateQuantity:
      // the swap detaches nodes, so asking again after the response could name a
      // different list than the one the server was asked to render.
      const sections = CartRegions.sectionsToRender();
      const body = JSON.stringify(
        Object.assign({}, payload, {
          sections: sections.map((entry) => entry.section),
          sections_url: window.location.pathname,
        })
      );

      inFlight = true;
      raced = false;
      setLocked(true);

      // One fresh photograph of every region, swapped in; the empty-state flags
      // come off the fresh DOM (a GET carries no item_count). Never rejects: a
      // refetch that fails leaves the regions as they were, logged.
      const refetch = () =>
        CartRegions.fetchAndRender(sections)
          .then(() => CartRegions.syncEmptyStateFromDom())
          .catch((error) => console.error(error));

      const announce = (variantId) =>
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-items',
          origin: 'a4',
          productVariantId: variantId,
        });

      const onState = (state) => {
        // /cart/add errors as {status, message, description}; /cart/change as
        // {errors} (Dawn's updateQuantity guards the same shape).
        if (state.status || state.errors) {
          const message = state.status === 422 && state.description ? state.description : generic();
          return refetch().then(() => {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'a4',
              productVariantId: opts.variantId,
              errors: message,
              message: state.message,
            });
            // The page just changed under the shopper: other listeners (the perk
            // meter) re-read the cart, and focus goes back where the tap was. The
            // error slot is role="alert", so no announcement is added to it.
            announce(opts.variantId);
            afterRender(opts, false);
            return { ok: false, message: message };
          });
        }

        // syncEmptyState AFTER the swap: the .drawer__inner render recreates
        // <cart-drawer-items>. /cart/add.js returns no item_count, and an add
        // can only leave the cart non-empty, so undefined === 0 is correctly false.
        let painted;
        if (state.sections) {
          CartRegions.renderSections(sections, state.sections);
          CartRegions.syncEmptyState(state.item_count === 0);
          painted = Promise.resolve();
        } else {
          painted = refetch();
        }

        return painted
          .then(() => (raced ? refetch() : undefined))
          .then(() => {
            announce(opts.variantId);
            afterRender(opts, true);
            return { ok: true, cart: state };
          });
      };

      return fetch(url, Object.assign(fetchConfig(), { body }))
        .then((response) => response.json())
        .then(onState, (error) => {
          // The request itself failed, or the body was not JSON (the bot wall's
          // HTML page). The write may or may not have landed: refetch, then say so.
          console.error(error);
          return refetch().then(() => ({ ok: false, message: generic() }));
        })
        .catch((error) => {
          console.error(error);
          return { ok: false, message: generic() };
        })
        .finally(() => {
          inFlight = false;
          raced = false;
          setLocked(false);
        });
    };

    return {
      get busy() {
        return busy();
      },

      /*
       * Adds ONE unit, the way every "+" in this offer does. Resolves (never
       * rejects) to {ok, cart} · {ok:false, message} · {ok:false, skipped:true}.
       * options: {inDrawer, refocus(), liveRegion, announce()} — both callbacks
       * run after the swap, against the new DOM.
       */
      add(variantId, options) {
        const payload = { items: [{ id: Number(variantId), quantity: 1 }] };
        return write(routes.cart_add_url, payload, Object.assign({ variantId: variantId }, options));
      },

      /* Sets one LINE to `quantity` (0 removes it). lineKey is line_item.key. */
      change(lineKey, quantity, options) {
        const payload = { id: String(lineKey), quantity: Number(quantity) };
        return write(routes.cart_change_url, payload, options);
      },
    };
  })();
}

/*
 * <a4-cart-tiles> — the tile row's tap handler. One per surface: the drawer's
 * and, on /cart, the cart page's too. The server renders every state; this only
 * turns a "+" tap into A4Cart.add(), then puts focus and the announcement back
 * on whichever surface the tap came from. The swap replaces this element, so
 * everything the follow-up needs is captured before the write.
 */
if (!customElements.get('a4-cart-tiles')) {
  customElements.define(
    'a4-cart-tiles',
    class A4CartTiles extends HTMLElement {
      constructor() {
        super();
        this.onClick = this.onClick.bind(this);
      }

      connectedCallback() {
        this.addEventListener('click', this.onClick);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
      }

      onClick(event) {
        const button = event.target.closest('[data-a4-add]');
        if (!button || !this.contains(button)) return;
        event.preventDefault();
        if (button.getAttribute('aria-disabled') === 'true' || window.A4Cart.busy) return;

        const variantId = button.dataset.variantId;
        const name = button.dataset.a4Name || '';
        const inDrawer = !!this.closest('cart-drawer');
        const scopeId = inDrawer ? 'CartDrawer' : 'main-cart-items';
        const scope = () => document.getElementById(scopeId);

        this.showError(null);
        button.classList.add('loading');

        // The drawer swap replaces this element, and an error now refetches the
        // drawer too (R-21), so the message goes to whichever <a4-cart-tiles> is in
        // the page when the write settles — the same tile row the shopper sees.
        const showError = (message) => {
          const current = scope() && scope().querySelector('a4-cart-tiles');
          (current || this).showError(message);
        };

        window.A4Cart.add(variantId, {
          inDrawer: inDrawer,
          liveRegion: inDrawer ? 'CartDrawer-LiveRegionText' : 'cart-live-region-text',
          // "Lagoon Blue added · 3 of 4" (§2.4.6). The count is the server's,
          // read off the re-rendered row, never computed here.
          announce: () => {
            const tiles = scope() && scope().querySelector('a4-cart-tiles');
            const status = tiles ? tiles.dataset.a4Status : '';
            return status ? `${name} added · ${status}` : `${name} added`;
          },
          // Same tile's "+" in the new row, so repeated adds keep their place;
          // else the row's own focus target (the bundle link, or its label);
          // else, in the drawer, its heading — never <body>.
          refocus: () => {
            const root = scope();
            if (!root) return null;
            const same = root.querySelector(`[data-a4-add][data-variant-id="${variantId}"]`);
            if (same) return same;
            const fallback = root.querySelector('a4-cart-tiles [data-a4-focus]');
            if (fallback) return fallback;
            if (!inDrawer) return root.querySelector('.cart-item__name');
            const heading = root.querySelector('.drawer__heading');
            if (heading) heading.setAttribute('tabindex', '-1');
            return heading;
          },
        })
          .then((result) => {
            if (!result.ok && !result.skipped) showError(result.message);
          })
          .finally(() => {
            button.classList.remove('loading');
          });
      }

      showError(message) {
        const slot = this.querySelector('[data-a4-error]');
        if (!slot) return;
        slot.toggleAttribute('hidden', !message);
        slot.textContent = message || '';
      }
    }
  );
}
